import { InformationCircleIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';

import DataTable from 'components/DataTable';

import Badge from 'primitives/Badge';
import { Dialog, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';

import { fixToTwoLocalPrice } from 'utils/format-helper';

import { useSyndicateAnalyticsData } from '.';

const formatCurrency = (value: number) => `₹ ${fixToTwoLocalPrice(Math.round(value))}`;

const formatPercentage = (value: number) => `${value.toFixed(2)}%`;

const formatMultiple = (value: number) => `${Number(value.toFixed(4))}x`;

const Companies: FC = () => {
  const { analytics } = useSyndicateAnalyticsData();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

  const selectedCompany = useMemo(
    () => analytics.companyMetrics.find(company => company.companyId === selectedCompanyId) || null,
    [analytics.companyMetrics, selectedCompanyId]
  );

  const sortedYearWiseValuations = useMemo(
    () =>
      selectedCompany
        ? [...selectedCompany.yearWiseValuations].sort((left, right) => right.year - left.year)
        : [],
    [selectedCompany]
  );

  const latestYearSnapshot = sortedYearWiseValuations[0] || null;

  return (
    <div className="space-y-6 mb-12">
      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Company-wise Aggregation</h2>
        <p className="mt-1 text-sm text-gray-500">
          Multiple scheme exposures into the same company are merged here, with year-wise fair
          market value snapshots.
        </p>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="p-6">
          <DataTable
            data={analytics.companyMetrics}
            searchFields={['companyName']}
            onClick={row => setSelectedCompanyId(row.companyId)}
            columns={[
              {
                label: 'Company',
                fieldName: '',
                type: 'CUSTOM',
                customValue: value => (
                  <div className="flex items-center gap-3">
                    {value.companyImage ? (
                      <img
                        src={value.companyImage}
                        alt={value.companyName}
                        className="h-9 w-9 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 font-medium text-gray-600">
                        {value.companyName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="font-medium text-gray-900">{value.companyName}</span>
                  </div>
                ),
              },
              {
                label: 'Schemes',
                fieldName: 'schemeNames',
                type: 'CUSTOM',
                customValue: value => (
                  <div className="flex flex-wrap gap-1">
                    {value.map((schemeName: string) => (
                      <Badge key={schemeName} label={schemeName} color="indigo" />
                    ))}
                  </div>
                ),
              },
              {
                label: 'Invested Capital',
                fieldName: 'investedCapital',
                type: 'CURRENCY',
              },
              {
                label: 'Current Value',
                fieldName: 'totalValue',
                type: 'CURRENCY',
              },
              {
                label: 'Markup %',
                fieldName: 'markupPercentage',
                type: 'PERCENTAGE',
              },
              {
                label: 'TVPI',
                fieldName: 'tvpi',
                type: 'MULTIPLE',
              },
            ]}
            emptyListTitle="No company analytics yet"
            emptyListDescription="Company aggregation will appear once this syndicate has finalized investments."
          />
        </div>
      </section>

      {selectedCompany && (
        <Dialog open={!!selectedCompany} onClose={() => setSelectedCompanyId('')}>
          <DialogPanel className="min-w-[50vw]">
            <DialogTitle>{selectedCompany.companyName}</DialogTitle>
            <DialogSubTitle>
              Investment timeline alongside year-wise fair market value snapshots and the exact
              deal-level calculations used for each snapshot.
            </DialogSubTitle>
            <div className="mt-6 space-y-6">
              {latestYearSnapshot && (
                <section className="space-y-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Latest Overview</h3>
                    <p className="text-sm text-gray-500">
                      Quick view of the latest available year and its overall fair market value
                      snapshot.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Latest Year
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {latestYearSnapshot.year}
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Invested Capital
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formatCurrency(latestYearSnapshot.investedCapital)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 flex items-center gap-1">
                        Fair Market Value
                        <span className="group relative inline-flex items-center">
                          <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                          <span className="invisible group-hover:visible absolute z-50 min-w-48 rounded bg-indigo-600 p-2 text-xs font-normal normal-case tracking-normal text-white left-5 top-1/2 -translate-y-1/2">
                            Based on the company's latest share price
                          </span>
                        </span>
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formatCurrency(latestYearSnapshot.valuation)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Latest Markup
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {formatPercentage(latestYearSnapshot.markupPercentage)}
                      </p>
                    </div>
                  </div>
                </section>
              )}

              <section className="space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Investment Timeline</h3>
                  <p className="text-sm text-gray-500">
                    Each investment into this company, grouped by scheme and investment year.
                  </p>
                </div>
                <DataTable
                  data={selectedCompany.investmentTimeline}
                  columns={[
                    {
                      label: 'Scheme',
                      fieldName: 'schemeName',
                      type: 'STRING',
                    },
                    {
                      label: 'Investment Date',
                      fieldName: 'investmentDate',
                      type: 'DATE',
                    },
                    {
                      label: 'Year',
                      fieldName: 'investmentYear',
                      type: 'STRING',
                    },
                    {
                      label: 'Invested Capital',
                      fieldName: 'investedCapital',
                      type: 'CURRENCY',
                    },
                  ]}
                />
              </section>

              <section className="space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Fair Market Value Snapshots
                  </h3>
                  <p className="text-sm text-gray-500">
                    Latest year first, with each snapshot showing the company total and the
                    deal-level fair market value used in the backend calculation.
                  </p>
                </div>

                <div className="space-y-4">
                  {sortedYearWiseValuations.map(snapshot => (
                    <div
                      key={snapshot.year}
                      className="rounded-2xl border border-gray-200 bg-gray-50 p-5"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{snapshot.year}</h4>
                          <p className="text-sm text-gray-500">
                            Aggregate view across all investments active by year-end.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              Invested Capital
                            </p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {formatCurrency(snapshot.investedCapital)}
                            </p>
                          </div>
                          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              Fair Market Value
                            </p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {formatCurrency(snapshot.valuation)}
                            </p>
                          </div>
                          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              Markup
                            </p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {formatPercentage(snapshot.markupPercentage)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-semibold text-gray-900">
                            Working Calculation
                          </h5>
                          <p className="text-xs text-gray-500">
                            Only deals with an applicable fair market value by this year-end are
                            included.
                          </p>
                        </div>

                        <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
                          <div className="hidden grid-cols-5 gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-500 md:grid">
                            <span>Scheme</span>
                            <span>Investment</span>
                            <span>Fair Market Value Used</span>
                            <span>Calculation</span>
                            <span>Markup</span>
                          </div>

                          <div className="divide-y divide-gray-200">
                            {snapshot.calculations.map(calculation => (
                              <div
                                key={`${snapshot.year}-${calculation.dealId}`}
                                className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-5"
                              >
                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 md:hidden">
                                    Scheme
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {calculation.schemeName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatCurrency(calculation.investedCapital)} invested
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 md:hidden">
                                    Investment
                                  </p>
                                  <p className="text-sm text-gray-900">
                                    {dayjs(calculation.investmentDate).format('D MMM YYYY')}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Investment year {calculation.investmentYear}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 md:hidden">
                                    Fair Market Value Used
                                  </p>
                                  <p className="text-sm text-gray-900">
                                    {dayjs(calculation.valuationEffectiveOn).format('D MMM YYYY')}
                                  </p>
                                  <p className="text-xs text-gray-500">Latest applicable update</p>
                                </div>

                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 md:hidden">
                                    Calculation
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(calculation.valuation)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatCurrency(calculation.investedCapital)} x{' '}
                                    {formatMultiple(calculation.valuationMultiple)} ={' '}
                                    {formatCurrency(calculation.valuation)}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 md:hidden">
                                    Markup
                                  </p>
                                  <p className="text-sm text-gray-900">
                                    {formatPercentage(calculation.markupPercentage)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </DialogPanel>
        </Dialog>
      )}
    </div>
  );
};

export default Companies;
